import React, {
    HTMLAttributes,
    MouseEvent,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react'
import { twMerge } from 'tailwind-merge'

import { createToggleableContext, useToggleableContext } from 'hooks'
import { Button } from '../Button'

import CloseIcon from 'assets/images/close-icon-thick.svg'
import styles from './style.module.css'

function stopPropagation(e: MouseEvent<HTMLDivElement>): void {
    e.stopPropagation()
}

const BottomSheetContext = createToggleableContext()

interface BottomSheetRootProps {
    readonly open?: boolean
    readonly children?: ReactNode
}

function BottomSheetRoot({
    open,
    children,
}: BottomSheetRootProps): JSX.Element {
    const context = useToggleableContext(open)

    return (
        <BottomSheetContext.Provider value={context}>
            {children}
        </BottomSheetContext.Provider>
    )
}

interface BottomSheetTriggerProps<T> extends HTMLAttributes<HTMLDivElement> {
    readonly meta?: T
}

function BottomSheetTrigger<T>({
    meta,
    onClick,
    className,
    ...props
}: BottomSheetTriggerProps<T>): JSX.Element {
    const context = useContext(BottomSheetContext)
    const onTriggerClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            context.toggle(undefined, meta)
            onClick?.(e)
        },
        [onClick, context]
    )

    return (
        <div
            onClick={onTriggerClick}
            className={twMerge('cursor-pointer', className)}
            {...props}
        />
    )
}

interface BottomSheetCloseButtonProps<T>
    extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
    readonly meta?: T
}

function BottomSheetCloseButton<T>({
    meta,
    className,
    ...props
}: BottomSheetCloseButtonProps<T>): JSX.Element {
    return (
        <BottomSheetTrigger
            {...props}
            meta={meta}
            onClick={stopPropagation}
            className={twMerge('my-2.5 mr-2.5 ml-auto w-fit', className)}
        >
            <Button
                variant="circle"
                size="icon"
                theme="black-on-white"
                className="drop-shadow-md"
            >
                <img src={CloseIcon} width={24} height={24} />
            </Button>
        </BottomSheetTrigger>
    )
}

interface BottomSheetProps<T>
    extends Omit<HTMLAttributes<HTMLDialogElement>, 'title'> {
    readonly meta?: T
    readonly open?: boolean
    readonly draggable?: boolean
    readonly modal?: boolean
    readonly hideOnBlur?: boolean
    readonly showCloseButton?: boolean
    readonly children: ReactNode
}

function BottomSheet<T>({
    meta,
    open,
    draggable,
    modal,
    hideOnBlur = true,
    showCloseButton,
    children,
    className,
    ...props
}: BottomSheetProps<T>): JSX.Element {
    const dialog = useRef<HTMLDialogElement>(null)
    const container = useRef<HTMLDivElement>(null)
    const context = useContext(BottomSheetContext)
    const isOpen = open ?? context.value

    const onBodyClose = useCallback(
        (e: MouseEvent<HTMLDialogElement>) => {
            if (modal && hideOnBlur && e.target !== container.current) {
                context.toggle(false, meta)
            }
        },
        [modal, hideOnBlur, meta, context.toggle]
    )

    const onCancel = useCallback(
        (e: SyntheticEvent<HTMLDialogElement>) => {
            e.preventDefault()

            if (modal && hideOnBlur) {
                context.toggle(false, meta)
            }
        },
        [modal, hideOnBlur, meta, context.toggle]
    )

    useEffect(() => {
        // If dialog element is not ready
        // Or if dialog is not a "modal", marking it as an inline sheet instead
        if (!dialog.current || !modal) return

        if (isOpen) {
            dialog.current.showModal()
            return
        }

        if (!dialog.current.open) {
            return
        }

        // Now, instead of just closing, we want to add animation to it
        const animation = dialog.current.animate(
            [
                {
                    opacity: 1,
                    transform: 'translateY(0px)',
                },
                {
                    opacity: 0.7,
                    transform: 'translateY(50px)',
                },
            ],
            {
                duration: 200,
                easing: 'ease-out',
            }
        )

        animation.onfinish =
            animation.oncancel =
            animation.onremove =
                () => {
                    dialog.current?.close()
                }
    }, [isOpen])

    return (
        <dialog
            ref={dialog}
            {...props}
            open={modal ? undefined : isOpen}
            className={twMerge(
                'p-0',
                'bg-transparent max-w-[unset]',
                'max-h-[37.5rem] w-[30rem]',
                modal ? '' : 'relative',
                modal ? 'sm:w-screen' : 'sm:max-h-[100dvh] sm:w-screen',
                modal
                    ? 'bottom-[calc((100vh_-_37.5rem)/2)] top-auto sm:top-auto sm:bottom-0'
                    : 'top-auto bottom-0',
                modal ? 'animation-bottom-push' : '',
                modal ? styles.overlay : ''
            )}
            // eslint-disable-next-line react/no-unknown-property
            onCancel={onCancel}
            onClick={onBodyClose}
        >
            {showCloseButton && modal && <BottomSheetCloseButton meta={meta} />}
            <div
                ref={container}
                onClick={stopPropagation}
                className={twMerge(
                    'flex flex-col p-4 w-full',
                    'bg-white shadow-lg rounded-t-lg',
                    modal ? 'rounded-b-lg sm:rounded-b-none' : '',
                    draggable ? 'pt-0' : '',
                    className
                )}
            >
                {draggable && (
                    <hr className="h-1 w-6 rounded-sm bg-neutral-400 mx-auto my-2 border-none" />
                )}
                <div className="overflow-auto">{children}</div>
            </div>
        </dialog>
    )
}

BottomSheet.Trigger = BottomSheetTrigger
BottomSheet.Root = BottomSheetRoot
BottomSheet.CloseButton = BottomSheetCloseButton
BottomSheet.Context = BottomSheetContext

export { BottomSheet }
